import { LegacyRef, useState } from 'react';
import { useChat, useIsChatLocked } from '../../../../../libs/hooks';
import { AuthorRoles, ChatMessageType, IChatMessage } from '../../../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../../../redux/app/hooks';
import { DefaultChatUser } from '../../../../../redux/features/app/AppState';
// import { timestampToDateString } from '../../../../utils/TextUtils';
import { useDispatch } from 'react-redux';
import { RootState } from '../../../../../redux/app/store';
import { setNotifications } from '../../../../../redux/features/app/appSlice';
import { copyImageOnClick, copyOnClick } from '../../../../utils/CopyUtils';
import { NOTIFICATION_TEMPLATES } from '../../Notifications/NotificationConstants';
import { Citations } from '../Citations/Citations';
import { PromptDialog } from '../PromptDialog/PromptDialog';
import { QRDialog } from '../QRDialog/QRDialog';
import { TypingIndicator } from '../TypingIndicator/TypingIndicator';
import * as utils from './../../../../utils/TextUtils';
import { timestampToDateString } from './../../../../utils/TextUtils';
import { ChatItemDocumentContent } from './ChatItemDocumentContent';
import { ChatItemImageContent } from './ChatItemImageContent';
import { ChatItemTextContent } from './ChatItemTextContent';
import { UserFeedbackActions } from './UserFeedbackActions';
import { UserFeedbackComment } from './UserFeedbackComment';

interface ChatItemProps {
    chatMode: string;
    message: IChatMessage;
    messageIndex: number;
    messageRef: LegacyRef<HTMLDivElement> | undefined;
}

export const ChatItem: React.FC<ChatItemProps> = ({ chatMode, message, messageIndex, messageRef }) => {
    const isBubbleChat = chatMode === 'Bubbles';
    const dispatch = useDispatch();
    //const classes = useClasses();
    const chat = useChat();

    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const { isLockedChat } = useIsChatLocked(conversations, selectedId);
    const isDefaultUser = message.userId === DefaultChatUser.id;
    const isMe = isDefaultUser || (message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id);
    const isBot = message.authorRole === AuthorRoles.Bot;
    const user = isDefaultUser
        ? DefaultChatUser
        : chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;

    const [messagedCopied, setMessageCopied] = useState(false);
    const [imageCopied, setImageCopied] = useState(false);
    const [showFeedbackComment, setShowFeedbackComment] = useState(false);
    const [promptDialogOpen, setPromptDialogOpen] = useState(false);
    const [QRDialogOpen, setQRDialogOpen] = useState(false);
    const { isQRCodeEnabled } = useAppSelector((state: RootState) => state.app);
    let content: JSX.Element;

    const downloadFile = () => {
        const binaryData = atob(message.content);
        const byteNumbers = new Array(binaryData.length).fill(0).map((_, i) => binaryData.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);

        // Create a Blob from the byte array
        const blob = new Blob([byteArray], { type: 'image/jpeg' });

        // Create a temporary URL for the Blob
        const blobURL = URL.createObjectURL(blob);

        // Create a link element and trigger the download
        const link = document.createElement('a');
        link.href = blobURL;
        link.download = `${message.id}`;
        document.body.appendChild(link); // Append to the body to make it work in all browsers
        link.click(); // Trigger the click to download
        document.body.removeChild(link); // Cleanup: remove the link after triggering the download

        // Release the blob URL
        URL.revokeObjectURL(blobURL);
    };

    if (message.type === ChatMessageType.Document) {
        content = <ChatItemDocumentContent isMe={isMe} message={message} />;
    } else if (message.type === ChatMessageType.Image) {
        content = <ChatItemImageContent message={message} isMe={false} />;
    } else {
        content =
            isBot && message.content.length === 0 ? <TypingIndicator /> : <ChatItemTextContent message={message} />;
    }
    const chatItemId: string = '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    );

    const avatar = isBot
        ? { image: { src: conversations[selectedId].botProfilePicture } }
        : isDefaultUser
          ? { idForColor: selectedId, color: 'colorful' }
          : { name: fullName, color: 'red' };

    const messages = conversations[selectedId].messages ?? [];
    const userFeedback = conversations[selectedId].userFeedback?.filter((f) => f.messageId === message.id)[0];
    const showUserFeedback =
        // features[FeatureKeys.UserFeedback].enabled &&
        messages.length > 1 &&
        message.tokenUsage &&
        (messageIndex === messages.length - 1 || userFeedback) &&
        message.userId === 'Bot';

    const actions = (
        <div className="ml-2 mt-1 text-slate-500">
            <div className={isBubbleChat ? 'flex gap-2 flex-row' : 'mb-2 flex w-full flex-row justify-end gap-x-2'}>
                {showUserFeedback && (
                    <UserFeedbackActions
                        message={message}
                        userFeedback={userFeedback}
                        onFeedbackTypeProvided={(feedbackType: boolean) => {
                            const notificationTemplate = feedbackType
                                ? NOTIFICATION_TEMPLATES.CHAT_MESSAGE_LIKED
                                : NOTIFICATION_TEMPLATES.CHAT_MESSAGE_DISLIKED;

                            dispatch(setNotifications([notificationTemplate]));

                            setShowFeedbackComment(true);
                        }}
                    />
                )}
                {isBot && message.content && message.tokenUsage && !isLockedChat && (
                    <button
                        title={messagedCopied ? 'Copied' : 'Copy text'}
                        type="button"
                        className={`dark:hover:text-slate-50 hover:text-primary-700 ${messagedCopied ? 'text-primary-400' : ''} transition-all duration-200`}
                        onClick={() => {
                            void copyOnClick(`#ci-${chatItemId}`, setMessageCopied, message.content);
                            dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_PROMPT_CHATMESSAGE }]));
                        }}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                            <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                        </svg>
                    </button>
                )}
                {isBot && message.content && message.type === ChatMessageType.Image && (
                    <>
                        <button
                            title={imageCopied ? 'Copied' : 'Copy image'}
                            type="button"
                            className={`dark:hover:text-slate-50 hover:text-primary-700 ${imageCopied ? 'text-primary-400' : ''} transition-colors duration-200`}
                            onClick={() => {
                                void copyImageOnClick(`${message.content}`, setImageCopied);
                                dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_PROMPT_CHATIMAGE }]));
                            }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                                <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                            </svg>
                        </button>
                        <button
                            title={'Download'}
                            type="button"
                            className={`dark:hover:text-slate-50 hover:text-primary-700 transition-colors duration-200`}
                            onClick={() => {
                                void downloadFile();
                                dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.DOWNLOAD_PROMPT_CHATIMAGE }]));
                            }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="h-5 w-5">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                        </button>
                    </>
                )}
                {isBot && message.prompt && (
                    <>
                        <button
                            title="Prompt"
                            className="dark:hover:text-slate-50 hover:text-primary-700 transition-colors duration-200"
                            type="button"
                            onClick={() => {
                                setPromptDialogOpen(true);
                            }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                                <path d="M12 8l.01 0" />
                                <path d="M11 12h1v4h1" />
                            </svg>
                        </button>
                        <PromptDialog
                            message={message}
                            open={promptDialogOpen}
                            onClose={() => setPromptDialogOpen(false)}
                        />
                    </>
                )}
                {isBot && isQRCodeEnabled && !isLockedChat && (
                    <>
                        <button
                            title="QR Code"
                            className="dark:hover:text-slate-50 hover:text-primary-700 transition-colors duration-200 m-0 p-0"
                            type="button"
                            onClick={() => {
                                setQRDialogOpen(true);
                            }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="h-5 w-5 m-0 p-0">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 3.75 9.375v-4.5ZM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5ZM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 13.5 9.375v-4.5Z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6.75 6.75h.75v.75h-.75v-.75ZM6.75 16.5h.75v.75h-.75v-.75ZM16.5 6.75h.75v.75h-.75v-.75ZM13.5 13.5h.75v.75h-.75v-.75ZM13.5 19.5h.75v.75h-.75v-.75ZM19.5 13.5h.75v.75h-.75v-.75ZM19.5 19.5h.75v.75h-.75v-.75ZM16.5 16.5h.75v.75h-.75v-.75Z"
                                />
                            </svg>
                        </button>
                        {QRDialogOpen && (
                            <QRDialog message={message} open={QRDialogOpen} onClose={() => setQRDialogOpen(false)} />
                        )}
                    </>
                )}
            </div>
            {/* {isBot && message.citations && <Citations citations={utils.getUsedCitations(message)} />} */}
        </div>
    );

if (message.type == ChatMessageType.Information)
{
    return (
        <div className="flex items-center justify-center my-4 px-4">
            <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
            <span className="px-4 text-xs text-slate-500 dark:text-slate-400 font-medium">
                {message.content}
            </span>
            <div className="flex-grow border-t border-slate-300 dark:border-slate-600"></div>
        </div>
    )
}

    return (
        <div
            ref={messageRef}
            className={
                isBubbleChat
                    ? 'flex-1 space-y-6 overflow-y-auto p-4 py-3 sm:py-4 text-sm leading-6 text-slate-900 dark:bg-slate-900 dark:text-slate-300 sm:leading-7'
                    : 'px-2 py-2 sm:px-4'
            }>
            {!isBubbleChat && actions}
            <div
                className={
                    isMe
                        ? isBubbleChat
                            ? 'flex flex-row-reverse items-start'
                            : 'flex px-4 py-4 sm:px-6'
                        : isBubbleChat
                          ? 'flex items-start'
                          : 'flex flex-row bg-slate-300 px-4 py-8 dark:bg-slate-800 sm:px-6 rounded-xl'
                }
                data-testid={`chat-history-item-${messageIndex}`}
                data-username={fullName}
                data-content={utils.formatChatTextContent(message)}>
                {
                    <div className="items-center mr-2 ml-2 hidden sm:flex">
                        {/* Avatar */}
                        <div className="relative w-8 h-8 flex items-center justify-center rounded-full overflow-hidden">
                            {avatar.image ? (
                                <img src={avatar.image.src} alt="Bot Avatar" className="w-full h-full object-cover" />
                            ) : (
                                <div
                                    className={`w-full h-full flex items-center justify-center ${
                                        isDefaultUser ? 'bg-' + avatar.color : 'bg-primary-500'
                                    } text-white font-bold`}>
                                    {avatar.name ? avatar.name.charAt(0) : 'U'}
                                </div>
                            )}
                        </div>
                    </div>
                }
                <div
                    className={
                        isMe
                            ? isBubbleChat
                                ? ''
                                : 'flex w-full flex-col items-start lg:flex-row lg:justify-between'
                            : isBubbleChat
                              ? 'flex flex-col items-end'
                              : 'flex items-start'
                    }>
                    <div
                        id={`#ci-${chatItemId}`}
                        className={
                            isMe
                                ? isBubbleChat
                                    ? 'flex flex-col rounded-b-xl rounded-tl-xl bg-slate-50 p-4 dark:bg-slate-800 sm:min-h-0 max-w-sm md:max-w-2xl'
                                    : 'max-w-3xl'
                                : isBubbleChat
                                  ? 'flex flex-col rounded-b-xl rounded-tr-xl bg-slate-50 p-4 dark:bg-slate-800 md:max-w-4xl lg:max-w-6xl'
                                  : ''
                        }>
                        {content}
                        <div className="text-xs flex flex-row text-slate-500 self-end mt-1">
                            {!isMe && fullName !== import.meta.env.VITE_APP_NAME && (
                                <span className="flex overflow-clip font-bold mr-1">{fullName}</span>
                            )}
                            <span className="flex overflow-clip">{timestampToDateString(message.timestamp, true)}</span>
                        </div>
                        {showFeedbackComment && messageIndex === messages.length - 1 && (
                            <div className="mt-2">
                                <UserFeedbackComment
                                    message={message}
                                    userFeedback={userFeedback}
                                    onFeedbackCommentProvided={() => {
                                        setShowFeedbackComment(false);
                                    }}
                                />
                            </div>
                        )}
                        {isBot && message.citations && <Citations citations={utils.getUsedCitations(message)} />}
                    </div>
                    {isBubbleChat && actions}
                </div>
            </div>
        </div>
    );
};
